import {useState, useEffect} from 'react'
import {
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom'

import {
  isSameDay,
  format,
  intervalToDuration,
  formatDuration,
  differenceInMinutes,
  isWithinInterval,
} from 'date-fns'

import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import AttentionIcon from '@mui/icons-material/Report'
import InfoIcon from '@mui/icons-material/Info'
import TickIcon from '@mui/icons-material/CheckCircle'
import Chip from '@mui/material/Chip';
import { Card, Grid, Box, Typography, Tooltip} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import {LiftsDoughnutGraph} from '_organisms'
import {Layout} from '_templates'
import {ReportsApi, sortByParam} from '_services'

const getInitialsFromFullName = (fullName) => {
  const splitName = fullName.split(' ')
  const nameCount = splitName.length
  const initials = splitName[0][0]
  if (nameCount > 1) {
    return initials + splitName[nameCount - 1][0]
  }
  return initials 
}

const StatusBadge = ({status, text}) => {
  let icon, color

  switch (status) {
    case 'warning':
      icon = <AttentionIcon />
      color = 'warning'
      break
    case 'critical':
      icon = <AttentionIcon />
      color = 'error'
      break
    case 'success':
      icon = <TickIcon />
      color = 'success'
      break
    default:
      icon = <InfoIcon />
      color = 'info'
  }

  return (
      <Chip
        icon={icon}
        color={color}
        label={text}
        size='small'
      />
  )
}

const getCustomer = (activity, company, status) => {
  let companyName, fullName, initials
  let plannedActivity = activity

  if (!activity || (status && !status.isPlanned)) return null

  if (activity.planned_plant_activity_id && activity.planned_plant_activity) {
    plannedActivity = activity.planned_plant_activity
  }

  if (!plannedActivity) return null

  if (plannedActivity.customer_type === 'User' && plannedActivity.user_customer?.employee) {
    const user = plannedActivity.user_customer
    const {name, surname} = user.employee
    fullName = `${name || ''} ${surname || ''}`.trim()
    initials = fullName ? getInitialsFromFullName(fullName) : ''
    companyName = user.employee.company_name || company?.name
  } else if (plannedActivity.public_user_customer) {
    const user = plannedActivity.public_user_customer
    fullName = user.full_name
    initials = fullName ? getInitialsFromFullName(fullName) : ''
    companyName = user.company_name
  }

  return fullName || companyName ? {companyName, fullName, initials} : null
}

const getNowFromOffset = (timezoneOffset) => {
  if (timezoneOffset === undefined || timezoneOffset === null) return new Date()
  const localOffsetMs = new Date().getTimezoneOffset() * 60 * 1000
  const targetOffsetMs = timezoneOffset * 60 * 1000
  return new Date(Date.now() + (targetOffsetMs - localOffsetMs))
}

const getFormattedTimeText = (timestamp, timezoneOffset) => {
  const timestampDate = new Date(timestamp)
  const isToday = isSameDay(timestampDate, getNowFromOffset(timezoneOffset))
  if (isToday) {
    return format(new Date(timestampDate), 'HH:mm')
  } else {
    return format(new Date(timestampDate), 'HH:mm ccc d MMM')
  }
}

const getFormattedDurationText = (start, end, style) => {

  const curtailDurationDetails = (duration) => {
    if (duration.years > 0) {
      duration = {
        years: duration.years,
        months: duration.months,
      }
    } else if (duration.months > 0) {
      duration = {
        months: duration.months,
        days: duration.days,
      }
    } else if (duration.days > 0) {
      duration = {
        days: duration.days,
        hours: duration.hours,
      }
    }
    return duration
  }

  let duration = intervalToDuration({start, end})
  duration = curtailDurationDetails(duration)

  let durationText = formatDuration(duration, {format: ['years','months','days', 'hours', 'minutes']})
  if (style === 'short') {
    durationText = durationText
      .replace(' minutes','m')
      .replace(' hours','h')
      .replace(' hour','h')
      .replace(' minute','h')
  } else {
    durationText = durationText
      .replace('minute', 'min')
      .replace('hour', 'hr')
  }
  return durationText
}


const getCurrentPlannedActivity = (plant, timezoneOffset) => {
  const now = getNowFromOffset(timezoneOffset)
  const plannedActivities = sortByParam(plant?.planned_plant_activities || [], 'start_time', 'asc')
  const linkedPlannedActivity = plant?.actual_plant_activities?.[0]?.planned_plant_activity

  if (linkedPlannedActivity) return linkedPlannedActivity

  return plannedActivities.find((activity) => {
    const start = new Date(activity.start_time)
    const end = new Date(activity.end_time)
    return isWithinInterval(now, { start, end })
  }) || plannedActivities[0] || null
}

const getActivityStatus = (activity, timezoneOffset) => {
  if (!activity) return null

  const MINUTES_OFF_SCHEDULE_THRESHOLD = 10 
  const MINUTES_SINCE_LAST_LIFT_STALE_THRESHOLD = 60
  const isPlanned = activity.planned_plant_activity_id !== null && !!activity.planned_plant_activity
  const plannedStartTime = isPlanned && new Date(activity.planned_plant_activity.start_time)
  const plannedEndTime = isPlanned && new Date(activity.planned_plant_activity.end_time)
  const startedAt = new Date(activity.started_at)
  const now = getNowFromOffset(timezoneOffset)
  const deliveriesCount = activity.plant_deliveries?.length || 0
  const lastDelivery = deliveriesCount ? activity.plant_deliveries[deliveriesCount - 1] : null
  const lastActionTime = lastDelivery?.delivered_at ? new Date(lastDelivery.delivered_at) : new Date(activity.started_at)
  const isDelay = activity.delay_reason_id !== null

  const isLate = 
    isPlanned && 
    differenceInMinutes(now, plannedEndTime) > MINUTES_OFF_SCHEDULE_THRESHOLD

  const isEarly = 
    isPlanned && 
    differenceInMinutes(plannedStartTime, now) > MINUTES_OFF_SCHEDULE_THRESHOLD

  const startedLate = 
    isPlanned && 
    differenceInMinutes(startedAt, plannedStartTime) > MINUTES_OFF_SCHEDULE_THRESHOLD

  const startedEarly = 
    isPlanned && 
    differenceInMinutes(plannedStartTime, startedAt) > MINUTES_OFF_SCHEDULE_THRESHOLD

  let staleSince = false
  if (differenceInMinutes(now, lastActionTime) > MINUTES_SINCE_LAST_LIFT_STALE_THRESHOLD) {
    staleSince = getFormattedDurationText(lastActionTime, now, 'long')
  }

  return {
    isPlanned,
    isDelay,
    startedLate,
    startedEarly,
    isLate,
    isEarly,
    staleSince, 
  }
}

const getActivityBannerTimeText = (activity, timezoneOffset) => {
  const startTime = new Date(activity.start_time)
  const endTime = new Date(activity.end_time)
  const startTimeText = getFormattedTimeText(startTime, timezoneOffset)
  const endTimeText = getFormattedTimeText(endTime, timezoneOffset)
  const durationText = getFormattedDurationText(startTime, endTime, 'long')
  return `${startTimeText} to ${endTimeText} (${durationText})`
}

const ActivityBanner = ({activity, status, plannedActivity, timezoneOffset}) => {
  const theme = useTheme()
  let bannerText = ''
  let badgeConfig = {}
  if (status) {
    if (status.isDelay) {
      const now = getNowFromOffset(timezoneOffset)
      const startTime = new Date(activity.started_at)
      const durationText = getFormattedDurationText(startTime, now, 'long')
      let plannedActivityText
      if (plannedActivity) {
        const plannedStartText = getFormattedTimeText(plannedActivity.start_time, timezoneOffset)
        const plannedEndText = getFormattedTimeText(plannedActivity.end_time, timezoneOffset)
        plannedActivityText = ', scheduled: ' + (plannedActivity.material?.name || 'Unknown material') + ' to ' + (plannedActivity.destination?.name || 'Unknown destination') + ' from ' + plannedStartText + ' to ' + plannedEndText
      } else {
        plannedActivityText = ', with no activity planned for now'
      }
      bannerText += durationText + plannedActivityText
      badgeConfig = {text: 'Delayed', status: 'warning'}
    } else {
      if (status.isPlanned) {
        if(status.isEarly || status.isLate) {
          if (plannedActivity){
            const plannedStartText = getFormattedTimeText(plannedActivity.start_time, timezoneOffset)
            const plannedEndText = getFormattedTimeText(plannedActivity.end_time, timezoneOffset)
            const plannedActivityText = (plannedActivity.material?.name || 'Unknown material') + ' to ' + (plannedActivity.destination?.name || 'Unknown destination') + ' from ' + plannedStartText + ' to ' + plannedEndText
            bannerText += 'Scheduled now: ' + plannedActivityText
          } else {
            bannerText += 'With no activity planned for now'
          }
          const badgeText = status.isEarly ? 'Early' : 'Late'
          badgeConfig = {text: badgeText, status: 'warning'}
        } else {
          badgeConfig = {text: 'On schedule', status: 'success'}
          bannerText += 'Planned: '
          const activityTimeText = activity.planned_plant_activity
            ? getActivityBannerTimeText(activity.planned_plant_activity, timezoneOffset)
            : ''
          bannerText += activityTimeText
        }
      } else {
        let plannedActivityText
        if (plannedActivity) {
          const plannedStartText = getFormattedTimeText(plannedActivity.start_time, timezoneOffset)
          const plannedEndText = getFormattedTimeText(plannedActivity.end_time, timezoneOffset)
          plannedActivityText = 'Scheduled now: ' + (plannedActivity.material?.name || 'Unknown material') + ' to ' + (plannedActivity.destination?.name || 'Unknown destination') + ' from ' + plannedStartText + ' to ' + plannedEndText
        } else {
          plannedActivityText = 'With no activity planned for now'
        }
        badgeConfig = {text: 'Unplanned', status: 'critical'}
        bannerText += plannedActivityText
      }
      if (status.staleSince) {
        badgeConfig = {text: 'Stale for ' + status.staleSince, status: 'critical'}
      }
    }
  } else {
    bannerText = activity ? getActivityBannerTimeText(activity, timezoneOffset) : ''
  }
  return (
    <Grid 
      item 
      xs={4}
      sx={{padding: '5px', backgroundColor: theme.palette.grey['300']}}
    >
      {status && <StatusBadge {...badgeConfig} />}
      <Tooltip title={bannerText}><Typography variant='body2' sx={{display: 'inline', whiteSpace: 'normal', wordBreak: 'break-word'}}> {bannerText} </Typography></Tooltip>
    </Grid>
  )
}

const ActivityDetailsSection = ({activity, company, currentActivityStatus, currentPlannedActivity, timezoneOffset}) => {

  const theme = useTheme()
  const isNow = !!currentActivityStatus
  const material = activity?.material?.name
  const destination = activity?.destination?.name
  const source = activity?.source?.name || 'anywhere'
  const {note} = activity

  const quantity = isNow ? activity.planned_plant_activity?.quantity : activity.quantity || 0
  const deliveryCount = activity.plant_deliveries_count

  let liftsText = isNow ? deliveryCount : quantity
  let liftsDoughnutGraphData = null

  if (currentActivityStatus?.isPlanned) {
    liftsText += '/' + quantity
    if (quantity && quantity > 0) {
      liftsDoughnutGraphData = {
        centerText: liftsText, 
        data: [
          {
            value: deliveryCount,
            color: theme.palette.success.dark,
          },
          {
            value: quantity - deliveryCount,
            color: theme.palette.grey['300'],
          },
        ]}
    }
  }

  const customer = getCustomer(activity, company, currentActivityStatus)
  const customerNameText = customer ? customer?.fullName : ''
  const companyNameText = customer ? `(${customer?.companyName})` : ''

  const renderCustomer = () => {
    const shouldShowCustomer = false
    return(
      <>
        {shouldShowCustomer && (
          <>
            <Box width='4px' sx={{marginRight: '-2px', marginLeft: '-2px', backgroundColor: theme.palette.grey['400']}} />
            <Grid 
              item 
              xs={5} 
              display='flex' 
              align='center' 
              justifyContent='center' 
            >
              <Grid container direction='column'>
                <Grid item xs={6}>
                  <Typography sx={{overflow: 'hidden', whiteSpace: 'nowrap'}}>{customerNameText}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{overflow: 'hidden', whiteSpace: 'nowrap'}}>{companyNameText}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </>
    )
  }

  const renderLiftsVisualization = () => {
    if (liftsDoughnutGraphData) {
      return <LiftsDoughnutGraph liftsDoughnutGraphData={liftsDoughnutGraphData} />
    } else {
      return <Typography variant='h5'>{liftsText}</Typography>
    }
  }

  const renderDetails = () => {
    if (currentActivityStatus?.isDelay) {
      return(
          <Grid 
            item 
            xs={12} 
            display='flex' 
            align='center' 
            justifyContent='center' 
          >
            <Typography>DELAYED: {activity.delay_reason?.name || 'Unknown reason'}</Typography>
          </Grid>
      )
    } else {
      return(
        <Grid container direction='row'>
          <Grid 
            item 
            xs={9} 
            display='flex' 
            align='center' 
            justifyContent='center' 
          >
            <Grid container direction='column'>
              <Grid item xs='auto'>
                <Tooltip title={material || ''}><Typography sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{material}</Typography></Tooltip>
              </Grid>
              <Grid item xs='auto'>
                <Tooltip title={`${source} to ${destination}`}><Typography sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{source} to {destination}</Typography></Tooltip>
              </Grid>
              {note && (
                <Grid item xs='auto'>
                  <Tooltip title={`note: ${note}`}><Typography variant='body2' sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>note: {note}</Typography></Tooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
          {renderCustomer()}
          <Box width='3px' sx={{marginRight: '-2px', marginLeft: '-2px', backgroundColor: theme.palette.grey['400']}} />
          <Grid 
            item 
            xs={3} 
            display='flex' 
            align='center' 
            justifyContent='center' 
            padding='5px'
          >
            <Grid container direction='column' >
              <Grid item xs='auto' >
                {renderLiftsVisualization()}
              </Grid>
              <Grid item xs='auto' >
                <Typography variant='body2' sx={{overflow: 'hidden', whiteSpace: 'nowrap'}}>lifts</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )
    }
  }

  return (
    <Grid item xs={9}>
      <Grid container direction='column'>
        <ActivityBanner activity={activity} status={currentActivityStatus} plannedActivity={currentPlannedActivity} timezoneOffset={timezoneOffset}/>
        <Grid 
          item 
          xs={8}
        >
          {renderDetails()}
        </Grid>
      </Grid>
    </Grid>
  )

}

const ActivityTimecountSection = ({activity, currentActivityStatus, currentPlannedActivity, timezoneOffset}) => {

  const theme = useTheme()
  const isNow = !!currentActivityStatus


  let timeTextParagraphs = []
  let badges = []

  if (!isNow) {
    const startTime = activity.start_time
    const timeToNext = getFormattedDurationText(getNowFromOffset(timezoneOffset), new Date(startTime), 'short')

    timeTextParagraphs.push({text: "Starts in:", variant: 'body2'})
    timeTextParagraphs.push({text: timeToNext, variant: 'h5'})
  } else {
    const {isLate, isEarly, startedLate, startedEarly, isPlanned} = currentActivityStatus


    if (isPlanned) {
      const plannedFinish = new Date(activity.planned_plant_activity.end_time)
      const plannedStart = new Date(activity.planned_plant_activity.start_time)
      const plannedFinishText = getFormattedTimeText(plannedFinish, timezoneOffset)
      const plannedStartText = getFormattedTimeText(plannedStart, timezoneOffset)
      const offScheduleText = 'Scheduled ' + plannedStartText + ' to ' + plannedFinishText

      let badgeTimeText = ''
      let badgeStatus = 'warning'

      if (startedEarly) {
        badgeTimeText += 'Early'
      } else if (startedLate) {
        badgeTimeText += 'Late'
      } else {
        badgeTimeText += 'On time'
        badgeStatus = 'success'
      }

      badgeTimeText += ' start'

      const startedAt = getFormattedTimeText(activity.started_at, timezoneOffset)
      badgeTimeText += ` (${startedAt})`
      badges.push({text: badgeTimeText, status: badgeStatus})

      if (isLate) {
        const timer = getFormattedDurationText(plannedFinish, getNowFromOffset(timezoneOffset), 'short')
        timeTextParagraphs.push({text: timer, variant: 'h5'})
        timeTextParagraphs.push({text: 'past planned finish', variant: 'body2'})
      } else if (isEarly) {
        const timer = getFormattedDurationText(getNowFromOffset(timezoneOffset), plannedStart, 'short')
        timeTextParagraphs.push({text: 'Due to start in',variant: 'body2'})
        timeTextParagraphs.push({text: timer, variant: 'h5'})
      } else {
        const timer = getFormattedDurationText(getNowFromOffset(timezoneOffset), plannedFinish, 'short')
        timeTextParagraphs.push({text: timer, variant: 'h5'})
        timeTextParagraphs.push({text: 'to go', variant: 'body2'})
      }

      if (isLate || isEarly) {
        timeTextParagraphs.push({text: offScheduleText, variant: 'body2'})
      }

    } else {
      const timer = getFormattedDurationText(new Date(activity.started_at), getNowFromOffset(timezoneOffset), 'short')
      timeTextParagraphs.push({text: timer, variant: 'h5'})
      timeTextParagraphs.push({text: 'and counting', variant: 'body2'})
    }

  }


  return (
    <>
      <Box width='3px' sx={{marginRight: '-3px', backgroundColor: theme.palette.grey['400']}} />
    <Grid 
      container
      xs={3} 
      display='flex' 
      align='center' 
      justifyContent='center' 
      direction='column'
    >
      {badges.map((b, i) => <StatusBadge key={i} text={b.text} status={b.status} />)}
      {timeTextParagraphs.map((p, i) => <Typography key={i} variant={p.variant}>{p.text}</Typography>)}
    </Grid>
    </>
  )

}

const getStatusColor = (status, theme) => {
  let color = theme.palette.grey['500']

  if (status) {
    const {
      isPlanned,
      isDelay,
      startedLate,
      startedEarly,
      isLate,
      isEarly,
      staleSince, 
    } = status 

    if (isDelay) {
      color = theme.palette.warning.main
    } else if (staleSince || !isPlanned) {
      color = theme.palette.red.main
    } else if (isLate || isEarly || startedLate || startedEarly) {
      color = theme.palette.warning.main
    } else {
      color = theme.palette.success.main
    }
  }
  return color
}

const PlantActivity = (props) => {
  const {activity, currentActivityStatus} = props

  const theme = useTheme()


  const isFutureActivity = !currentActivityStatus

  let status = currentActivityStatus ? {...currentActivityStatus} : null

  if (status) {
    delete status.startedLate
    delete status.startedEarly
  }

  const borderColor = getStatusColor(status, theme)
  const borderFormat = `5px solid ${borderColor}`

  if (!activity && isFutureActivity) {
    return (
      <Card raised={true} sx={{padding: '10px'}}>
        <Typography>No future activities planned</Typography>
      </Card>
    )
  }

  return (
    <Card raised={true} sx={{borderTop: borderFormat}}>
      <Grid container spacing={2} direction='row'>
        <ActivityDetailsSection {...props}/>
        <ActivityTimecountSection {...props}/>
      </Grid>
    </Card>
  )
}

const PlantSummary = ({plant, company, timezoneOffset}) => {
  const plannedActivities = sortByParam(plant?.planned_plant_activities || [], 'start_time', 'asc')
  const currentActivity = plant?.actual_plant_activities?.[0]
  const currentPlannedActivity = getCurrentPlannedActivity(plant, timezoneOffset)
  const nextActivity = plannedActivities.filter((activity) => currentPlannedActivity ? activity.id !== currentPlannedActivity.id : true)[0]
  const currentActivityStatus = getActivityStatus(currentActivity, timezoneOffset)

  return (
    <Grid item tvScreen={4} xs={4}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='h5'>{plant?.name}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>Now</Typography>
            <PlantActivity 
              activity={currentActivity} 
              currentActivityStatus={currentActivityStatus}
              company={company}
              currentPlannedActivity={currentPlannedActivity}
              timezoneOffset={timezoneOffset}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography>Next</Typography>
            <PlantActivity 
              activity={nextActivity} 
              activityType='next'
              company={company}
              timezoneOffset={timezoneOffset}
            />
          </Grid>
        </Grid>
    </Grid>
  )
}


const LivePlantDashboard = (props) => {
  const [zoomLevel, setZoomLevel] = useState(null)
  const navigate = useNavigate();
  const location = useLocation()

  const storedZoomLevel = localStorage.getItem('liveDashboardZoom') || '100'

  useEffect(() => {
    if (zoomLevel) {
      document.body.style.zoom = `${zoomLevel}%`;
    } else if (storedZoomLevel){
      document.body.style.zoom = `${storedZoomLevel}%`;
    }

    return () => {
      document.body.style.zoom = '100%';
    }
  }, [storedZoomLevel, zoomLevel]);


  const increaseZoomLevel = () => {
    const oldZoom = zoomLevel || storedZoomLevel/1 || 100
    const newZoom = oldZoom + 5
    setZoomLevel(newZoom)
    localStorage.setItem('liveDashboardZoom', newZoom)
  }

  const decreaseZoomLevel = () => {
    const oldZoom = zoomLevel || storedZoomLevel/1 || 100
    const newZoom = Math.max(5, oldZoom - 5)
    setZoomLevel(newZoom)
    localStorage.setItem('liveDashboardZoom', newZoom)
  }

  let {projectId} = useParams()
  const {useGetLivePlantDashboardQuery} = ReportsApi

  const timezoneOffset = new Date().getTimezoneOffset()
  const {data: liveDashboardData, isFetching: isLoading, error, fulfilledTimeStamp}= useGetLivePlantDashboardQuery({projectId, timezoneOffset}, {pollingInterval: 60000})
  const authError = error && error.originalStatus === 401 

  const lastPollText = fulfilledTimeStamp ? format(new Date(fulfilledTimeStamp), 'HH:mm ccc dd MMM') : 'Loading...'

  useEffect(() => {
    if (authError) {
      navigate('/login', {replace: true, state: {from: location}})
    }
  }, [authError, location, navigate])

  const company = liveDashboardData?.company
  const projectName = liveDashboardData?.name || ''

  const renderPlants = (plants) => {
    if (plants) {
      const sortedPlants = sortByParam(plants, 'name', 'asc')
      return sortedPlants.map((plant) => {
        const hasCurrentActivity = (plant.actual_plant_activities || []).length > 0
        const hasPlannedActivity = (plant.planned_plant_activities || []).length > 0

        if (hasCurrentActivity || hasPlannedActivity) {
          return (
            <PlantSummary key={plant.id} plant={plant} company={company} timezoneOffset={timezoneOffset}/>
          )
        } else return null
      })
    } else return null
  }

  return(
    <Layout
      title={projectName ? `Live dashboard - ${projectName}` : 'Live dashboard'}
      isLoading={isLoading}
      company={company}
      maxWidth='false'
      productType='lnb'
    >
      {!isLoading && !authError &&
      <Box>
        <Typography variant='body2'>Last updated: {lastPollText}</Typography>
        <Stack spacing={2} direction='row'>
          <Button margin='10px' variant='outlined' size='large' onClick={increaseZoomLevel} >+</Button>
          <Button variant='outlined' size='large' onClick={decreaseZoomLevel} >-</Button>
        </Stack>
        <Grid container spacing={2}>
          {renderPlants(liveDashboardData?.plants)}
        </Grid>
      </Box>
      }
    </Layout>
  )
}


export default LivePlantDashboard
